import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';

// MSAL Configuration
var redirectUri = "http://localhost:4030";
var clientId = "d9b78d8b-78ae-43d4-b9ea-3fed1e225e1f";
var apiScope = "api://422132b5-d04c-4e43-a6db-6651f01a1109/Read"; //sa-accounts-api-prd

if (process.env.REACT_APP_STAGE === "Dev") {
  redirectUri = "https://app-accounts-manager-dev.azurewebsites.net";
  clientId = "c515f30d-773d-4d01-bd78-7bd234e68611"; //sa-accounts-line-manager-approval-portal-sso-dev
  apiScope = "api://b05c5f1c-6e9d-46ff-9ca5-08702b91c8e7/Read"; //sa-accounts-api-dev
}
if (process.env.REACT_APP_STAGE === "Test") {
  redirectUri = "https://tst.accounts-linemanager.hafnia.com";
  clientId = "d9b78d8b-78ae-43d4-b9ea-3fed1e225e1f"; //sa-accounts-line-manager-approval-portal-sso-tst
  apiScope = "api://43c4cdb2-922e-4ff5-83e2-1967c8375573/Read"; //sa-accounts-api-tst
} else if (process.env.REACT_APP_STAGE === "Pre-Production") {
  redirectUri = "https://hafniabw-account-approval-line-pre.azurewebsites.net";
  clientId = "b5e9533d-cad1-4881-9cea-b1d554375fec";
} else if (process.env.REACT_APP_STAGE === "Production") {
  redirectUri = "https://accounts-linemanager.hafnia.com";
  clientId = "b5e9533d-cad1-4881-9cea-b1d554375fec";
}

export const msalConfig = {
  auth: {
    authority: "https://login.microsoftonline.com/a364eb28-e95b-4ad0-a4fb-5b4f7767ad84",
    clientId: clientId,
    redirectUri: redirectUri,
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false
  }
};

export const loginRequest = {
  scopes: [
    apiScope, // API SCOPE
  ]
};

// Auth Context
export const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
  const { instance, accounts } = useMsal();
  const [token, setToken] = useState(null);

  const userAccount = accounts.length > 0 ? accounts[0] : null;
  const isAuthenticated = !!userAccount;

  const acquireToken = useCallback(async () => {
    if (!userAccount) return null;

    try {
      const request = {
        ...loginRequest,
        account: userAccount
      };
      const response = await instance.acquireTokenSilent(request);
      setToken(response.accessToken);
      return response.accessToken;
    } catch (error) {
      console.error('Silent token acquisition failed, attempting popup:', error);
      try {
        const requestRetry = {
          ...loginRequest,
          account: userAccount
        };
        const response = await instance.acquireTokenPopup(requestRetry);
        setToken(response.accessToken);
        return response.accessToken;
      } catch (popupError) {
        console.error('Token acquisition failed:', popupError);
        return null;
      }
    }
  }, [instance, userAccount]);

  const login = useCallback(async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login failed:', error);
    }
  }, [instance]);

  const logout = useCallback(async () => {
    try {
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
      setToken(null);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }, [instance]);

  // Acquire token when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      acquireToken().catch(err => {
        console.error("Failed to acquire token on init:", err);
      });
    } else {
      setToken(null);
    }
  }, [isAuthenticated, acquireToken]);

  const contextValue = useMemo(() => ({
    token,
    isAuthenticated,
    login,
    logout,
    acquireToken,
    account: userAccount
  }), [token, isAuthenticated, userAccount, login, logout, acquireToken]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
